<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="600"
    scrollable
    persistent>
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-toolbar-title>{{
          $t('sharedAccounts.borrow.title')
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-stepper v-model="step" vertical flat class="elevation-0">
          <!-- Step 1: session -->
          <v-stepper-step :complete="step > 1" step="1" editable>
            {{ $t('sharedAccounts.borrow.step1') }}
            <small v-if="selectedSession" class="mt-1">
              {{ sessionLabel(selectedSession) }}
            </small>
          </v-stepper-step>
          <v-stepper-content step="1">
            <v-text-field
              v-model="sessionSearch"
              :placeholder="$t('sharedAccounts.borrow.searchSession')"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              class="mb-2" />
            <div v-if="sessionsLoading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="24" />
            </div>
            <v-list v-else-if="filteredSessions.length" dense class="py-0">
              <v-list-item
                v-for="s in filteredSessions"
                :key="s.id"
                @click="selectSession(s)">
                <v-list-item-icon class="mr-3">
                  <v-icon>mdi-monitor</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ pcName(s) }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    guestName(s)
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <p v-else class="text--secondary text-center py-4 mb-0">
              {{ $t('sharedAccounts.borrow.noSessions') }}
            </p>
          </v-stepper-content>

          <!-- Step 2: game then launcher -->
          <v-stepper-step
            :complete="step > 2"
            step="2"
            :editable="!!selectedSession">
            {{ $t('sharedAccounts.borrow.step2') }}
            <small v-if="selectedGame && selectedLauncher" class="mt-1">
              {{ selectedGame.name }} · {{ selectedLauncher.name }}
            </small>
          </v-stepper-step>
          <v-stepper-content step="2">
            <!-- This session already holds borrowed account(s): show creds -->
            <template v-if="borrowedOnSession.length">
              <v-alert type="info" text dense class="mb-2">
                {{ $t('sharedAccounts.borrow.alreadyBorrowed') }}
              </v-alert>
              <v-list dense class="py-0 mb-2">
                <v-list-item
                  v-for="acc in borrowedOnSession"
                  :key="acc.id"
                  class="px-0">
                  <v-list-item-avatar
                    v-if="acc.app && acc.app.icon"
                    size="22"
                    tile>
                    <v-img :src="acc.app.icon" contain />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ acc.username }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ acc.app && acc.app.name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      small
                      color="primary"
                      :loading="borrowing"
                      :disabled="borrowing || !hasBorrowableGame(acc)"
                      @click="showExisting(acc)">
                      {{ $t('sharedAccounts.borrow.showCredentials') }}
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-divider class="my-3" />
              <div class="caption text--secondary mb-2">
                {{ $t('sharedAccounts.borrow.orBorrowNew') }}
              </div>
            </template>

            <v-text-field
              v-model="gameSearch"
              :placeholder="$t('sharedAccounts.borrow.searchGame')"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              class="mb-2" />

            <div v-if="allAccountsLoading" class="text-center py-3">
              <v-progress-circular indeterminate color="primary" size="22" />
            </div>
            <template v-else>
              <div class="caption text--secondary mb-1">
                {{
                  isSearchingGames
                    ? $t('sharedAccounts.borrow.selectGame')
                    : $t('sharedAccounts.borrow.popularGames')
                }}
              </div>
              <v-chip-group
                v-if="displayedGames.length"
                v-model="selectedGameId"
                column
                active-class="primary--text">
                <v-chip
                  v-for="g in displayedGames"
                  :key="g.game_id"
                  :value="g.game_id"
                  small
                  outlined
                  @click="selectGame(g)">
                  {{ g.name }}
                </v-chip>
              </v-chip-group>
              <p
                v-else-if="isSearchingGames"
                class="text--secondary caption mb-2">
                {{ $t('components.noData.noEntriesFound') }}
              </p>
            </template>

            <template v-if="selectedGame">
              <v-divider class="my-3" />

              <!-- Game on 2+ launchers: support must pick which one -->
              <template v-if="launchers.length > 1">
                <div class="caption text--secondary mb-1">
                  {{ $t('sharedAccounts.borrow.selectLauncher') }}
                </div>
                <v-list dense class="py-0">
                  <v-list-item
                    v-for="l in launchers"
                    :key="l.app_id"
                    :input-value="
                      selectedLauncher && selectedLauncher.app_id === l.app_id
                    "
                    @click="selectedLauncher = l">
                    <v-list-item-avatar v-if="l.icon" size="24" tile>
                      <v-img :src="l.icon" contain />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ l.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip
                        x-small
                        :color="l.available ? 'success' : ''"
                        :text-color="l.available ? 'white' : ''"
                        :outlined="!l.available">
                        {{
                          l.available
                            ? $t('sharedAccounts.borrow.available', {
                                count: l.available,
                              })
                            : $t('sharedAccounts.borrow.noneAvailable')
                        }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </template>

              <!-- Single launcher: auto-selected, just shown for context -->
              <div
                v-else-if="launchers.length === 1"
                class="d-flex align-center">
                <v-avatar v-if="launchers[0].icon" size="20" class="mr-2" tile>
                  <v-img :src="launchers[0].icon" contain />
                </v-avatar>
                <span class="body-2">{{ launchers[0].name }}</span>
                <v-spacer />
                <v-chip
                  x-small
                  :color="launchers[0].available ? 'success' : ''"
                  :text-color="launchers[0].available ? 'white' : ''"
                  :outlined="!launchers[0].available">
                  {{
                    launchers[0].available
                      ? $t('sharedAccounts.borrow.available', {
                          count: launchers[0].available,
                        })
                      : $t('sharedAccounts.borrow.noneAvailable')
                  }}
                </v-chip>
              </div>

              <p v-else class="text--secondary caption mb-0">
                {{ $t('sharedAccounts.borrow.noneAvailable') }}
              </p>
            </template>

            <v-btn
              color="primary"
              block
              class="mt-4"
              :loading="borrowing"
              :disabled="!canBorrow || borrowing"
              @click="doBorrow">
              {{ $t('sharedAccounts.borrow.borrowBtn') }}
            </v-btn>
          </v-stepper-content>

          <!-- Step 3: credentials -->
          <v-stepper-step step="3" :complete="step > 3">
            {{ $t('sharedAccounts.borrow.step3') }}
          </v-stepper-step>
          <v-stepper-content step="3">
            <v-alert v-if="borrowError" type="error" text dense>
              {{ borrowError }}
            </v-alert>

            <div v-for="acct in resultAccounts" :key="acct.key" class="mb-4">
              <div class="overline">{{ acct.label }}</div>

              <!-- 2FA hero -->
              <v-card outlined class="mb-3 pa-3 text-center two-fa">
                <div class="caption text--secondary">
                  {{ $t('sharedAccounts.borrow.twoFa') }}
                </div>
                <div v-if="!acct.id" class="caption text--secondary my-2">
                  {{ $t('sharedAccounts.borrow.twoFaUnavailable') }}
                </div>
                <template v-else-if="twoFaOf(acct).code">
                  <div class="two-fa__code">{{ twoFaOf(acct).code }}</div>
                  <div class="caption text--secondary">
                    {{
                      $t('sharedAccounts.borrow.twoFaExpiresIn', {
                        time: countdown(acct),
                      })
                    }}
                  </div>
                  <v-btn
                    text
                    x-small
                    color="primary"
                    @click="copy(twoFaOf(acct).code)">
                    <v-icon x-small left>mdi-content-copy</v-icon>
                    {{ $t('sharedAccounts.borrow.copy') }}
                  </v-btn>
                </template>
                <template v-else-if="twoFaOf(acct).status === 'error'">
                  <div class="error--text caption my-2">
                    {{ $t('sharedAccounts.borrow.twoFaError') }}
                  </div>
                  <v-btn x-small outlined @click="startTwoFa(acct)">
                    {{ $t('components.noData.tryAgain') }}
                  </v-btn>
                </template>
                <div v-else class="my-2">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="20" />
                  <div class="caption text--secondary mt-1">
                    {{ $t('sharedAccounts.borrow.twoFaWaiting') }}
                  </div>
                </div>
              </v-card>

              <!-- Credential rows -->
              <CredentialRow
                :label="$t('sharedAccounts.borrow.username')"
                :value="acct.data.username"
                @copy="copy" />
              <CredentialRow
                :label="$t('sharedAccounts.borrow.email')"
                :value="acct.data.email"
                @copy="copy" />
              <CredentialRow
                :label="$t('sharedAccounts.borrow.password')"
                :value="acct.data.password"
                secret
                @copy="copy" />
              <CredentialRow
                :label="$t('sharedAccounts.borrow.secret')"
                :value="acct.data.secret"
                secret
                @copy="copy" />
            </div>

            <p
              v-if="resultAccounts.length"
              class="caption text--secondary mb-1 mt-2">
              {{ $t('sharedAccounts.borrow.autoReturns') }}
            </p>
            <div class="d-flex align-center">
              <v-btn
                v-if="resultAccounts.length"
                text
                small
                color="error"
                :loading="returning"
                @click="releaseEarly">
                <v-icon x-small left>mdi-logout-variant</v-icon>
                {{ $t('sharedAccounts.borrow.releaseEarly') }}
              </v-btn>
              <v-spacer />
              <v-btn color="primary" @click="close">
                {{ $t('sharedAccounts.borrow.done') }}
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>

    <!-- Styled confirm for the destructive "release early" action -->
    <v-dialog v-model="confirmRelease" max-width="380" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('sharedAccounts.borrow.releaseEarly') }}
        </v-card-title>
        <v-card-text>
          {{ $t('sharedAccounts.borrow.releaseConfirm') }}
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text :disabled="returning" @click="confirmRelease = false">
            {{ $t('miscellaneous.cancel') }}
          </v-btn>
          <v-btn color="error" :loading="returning" @click="doRelease">
            {{ $t('sharedAccounts.borrow.releaseEarly') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import CredentialRow from './CredentialRow.vue'
import SessionsApi from '@/services/main/SessionsApi'
import SharedAccount from '@/models/configuration/SharedAccount'
import SharedAccountsApi from '@/services/configuration/SharedAccountsApi'
import { isGameActivelyBanned } from '@/utils/shared-accounts'
import { mapActions } from 'vuex'

const POLL_MS = 30000 // how often we poll for the 2FA code
const POLL_MAX_MS = 5 * 60 * 1000 // give up polling after this long
const TWO_FA_TTL_MS = 5 * 60 * 1000 // approx validity of a fetched 2FA code

export default {
  name: 'ManualBorrowDialog',
  components: { CredentialRow },
  props: {
    value: { type: Boolean, default: false },
    arenaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      step: 1,
      sessions: [],
      sessionsLoading: false,
      sessionSearch: '',
      selectedSession: null,
      gameSearch: '',
      allAccounts: [],
      allAccountsLoading: false,
      selectedGameId: null,
      selectedGame: null,
      launchers: [],
      selectedLauncher: null,
      borrowing: false,
      borrowError: '',
      result: null,
      twoFa: {},
      pollTimers: {},
      nowTick: 0,
      tickTimer: null,
      returning: false,
      confirmRelease: false,
      // app|game|session of the last borrow this open, to avoid re-firing it.
      lastBorrowKey: null,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    filteredSessions() {
      const term = (this.sessionSearch || '').trim().toLowerCase()
      if (!term) return this.sessions
      return this.sessions.filter(
        (s) =>
          this.pcName(s).toLowerCase().includes(term) ||
          this.guestName(s).toLowerCase().includes(term),
      )
    },
    // Client-side "contains" search over games in the arena's shared accounts.
    // We search locally because the backend game_name filter is prefix-based
    // (e.g. "Auto" wouldn't match "Grand Theft Auto V").
    candidateGames() {
      const term = (this.gameSearch || '').trim().toLowerCase()
      if (term.length < 2) return []
      const byGame = new Map()
      for (const acc of this.allAccounts) {
        if (acc.banned) continue // banned accounts can't be borrowed
        for (const g of acc.games || []) {
          if (
            g.game_id &&
            (g.name || '').toLowerCase().includes(term) &&
            !byGame.has(g.game_id)
          ) {
            byGame.set(g.game_id, { game_id: g.game_id, name: g.name })
          }
        }
      }
      return [...byGame.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 50)
    },
    // Most-stocked games (held by the most shared accounts) - shown as quick
    // suggestions before the user types. Always borrowable since they come from
    // the accounts themselves.
    popularGames() {
      const counts = new Map()
      for (const acc of this.allAccounts) {
        if (acc.banned) continue // banned accounts can't be borrowed
        for (const g of acc.games || []) {
          if (!g.game_id) continue
          const entry = counts.get(g.game_id) || {
            game_id: g.game_id,
            name: g.name,
            count: 0,
          }
          entry.count += 1
          counts.set(g.game_id, entry)
        }
      }
      return [...counts.values()]
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .slice(0, 8)
    },
    isSearchingGames() {
      return (this.gameSearch || '').trim().length >= 2
    },
    displayedGames() {
      return this.isSearchingGames ? this.candidateGames : this.popularGames
    },
    canBorrow() {
      return !!(
        this.selectedSession &&
        this.selectedGame &&
        this.selectedLauncher
      )
    },
    // Accounts already borrowed by the selected session (idempotent re-borrow
    // returns their credentials — used when kiosk auto-login failed post-borrow).
    borrowedOnSession() {
      const sid = this.selectedSession && String(this.selectedSession.id)
      if (!sid) return []
      return this.allAccounts.filter(
        (a) => String(a.borrowed_by_session_id) === sid,
      )
    },
    resultAccounts() {
      if (!this.result) return []
      const out = []
      if (this.result.first) {
        out.push({
          key: 'first',
          label: this.$t('sharedAccounts.borrow.firstAccount'),
          data: this.result.first,
          id: this.accountId(this.result.first),
        })
      }
      if (this.result.second) {
        out.push({
          key: 'second',
          label: this.$t('sharedAccounts.borrow.secondAccount'),
          data: this.result.second,
          id: this.accountId(this.result.second),
        })
      }
      return out
    },
  },
  watch: {
    value(open) {
      if (open) this.init()
      else this.teardown()
    },
    // Changing/clearing the search invalidates any previously picked game &
    // launcher, so stale selections (and an enabled Borrow) don't linger.
    gameSearch() {
      this.selectedGame = null
      this.selectedGameId = null
      this.selectedLauncher = null
      this.launchers = []
    },
  },
  beforeDestroy() {
    this.teardown()
  },
  methods: {
    ...mapActions(['showSnackbar']),
    init() {
      this.reset()
      this.fetchSessions()
      this.fetchAllAccounts()
    },
    reset() {
      this.step = 1
      this.sessionSearch = ''
      this.selectedSession = null
      this.gameSearch = ''
      this.allAccounts = []
      this.selectedGameId = null
      this.selectedGame = null
      this.launchers = []
      this.selectedLauncher = null
      this.borrowing = false
      this.borrowError = ''
      this.result = null
      this.twoFa = {}
      this.confirmRelease = false
      this.lastBorrowKey = null
    },
    teardown() {
      Object.values(this.pollTimers).forEach((t) => clearInterval(t))
      this.pollTimers = {}
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
      // Don't keep credentials/2FA codes in memory once the dialog is gone.
      this.result = null
      this.twoFa = {}
      this.lastBorrowKey = null
    },
    close() {
      this.isOpen = false
    },
    // ---- sessions ----
    async fetchSessions() {
      if (!this.arenaId) return
      this.sessionsLoading = true
      try {
        const response = await SessionsApi.getByArena(
          this.arenaId,
          'deviceId=computers&active=true&size=200',
        )
        this.sessions = response.data?.data || []
      } catch (e) {
        this.sessions = []
      } finally {
        this.sessionsLoading = false
      }
    },
    pcName(s) {
      return (s && s.device && s.device.hostname) || '—'
    },
    guestName(s) {
      return (s && s.user && s.user.username) || ''
    },
    sessionLabel(s) {
      const guest = this.guestName(s)
      return guest ? `${this.pcName(s)} · ${guest}` : this.pcName(s)
    },
    selectSession(s) {
      this.selectedSession = s
      this.step = 2
    },
    // ---- games / launchers (searched client-side over allAccounts) ----
    async fetchAllAccounts() {
      if (!this.arenaId) return
      this.allAccountsLoading = true
      try {
        // The list endpoint caps size at 100, so page through to load all.
        const size = 100
        let page = 1
        let all = []
        let count = Infinity
        while (all.length < count && page <= 20) {
          const response = await SharedAccount.api.list(
            this.arenaId,
            `page=${page}&size=${size}&sort=username`,
          )
          const data = response.data || {}
          const batch = data.data || []
          all = all.concat(batch)
          count = (data.metadata && data.metadata.count) || all.length
          if (!batch.length) break
          page += 1
        }
        this.allAccounts = all
      } catch (e) {
        this.allAccounts = []
      } finally {
        this.allAccountsLoading = false
      }
    },
    selectGame(g) {
      this.selectedGame = g
      this.selectedGameId = g.game_id
      this.selectedLauncher = null
      // Derive launchers from all loaded accounts (includes in-use ones, so a
      // fully-borrowed game still resolves its launcher).
      const byApp = new Map()
      for (const acc of this.allAccounts) {
        const app = acc.app
        if (!app || !app.id) continue
        const game = (acc.games || []).find((x) => x.game_id === g.game_id)
        if (!game) continue
        if (!byApp.has(app.id)) {
          byApp.set(app.id, {
            app_id: app.id,
            name: app.name,
            icon: app.icon || '',
            available: 0,
          })
        }
        if (
          !acc.borrowed_by_session_id &&
          !acc.banned &&
          !isGameActivelyBanned(game)
        ) {
          byApp.get(app.id).available += 1
        }
      }
      this.launchers = [...byApp.values()].sort(
        (a, b) => b.available - a.available || a.name.localeCompare(b.name),
      )
      // Usually a single launcher -> pick it automatically; only ask when 2+.
      if (this.launchers.length === 1) {
        this.selectedLauncher = this.launchers[0]
      }
    },
    // ---- borrow ----
    accountId(data) {
      return data && (data.shared_account_id || data.id)
    },
    hasBorrowableGame(account) {
      const appId = account.app_id || (account.app && account.app.id)
      const gameId =
        account.games && account.games[0] && account.games[0].game_id
      return !!(appId && gameId)
    },
    // Re-borrow (idempotent per session) the account this session already holds,
    // to retrieve its credentials. We pass the account's launcher + one of its
    // games; same session_id returns that same account without double-borrowing.
    showExisting(account) {
      const appId = account.app_id || (account.app && account.app.id)
      const game = account.games && account.games[0]
      if (!appId || !game) return
      this.selectedGame = { game_id: game.game_id, name: game.name }
      this.selectedLauncher = {
        app_id: appId,
        name: account.app && account.app.name,
        icon: (account.app && account.app.icon) || '',
        available: 0,
      }
      this.doBorrow()
    },
    async doBorrow() {
      if (!this.canBorrow || this.borrowing) return
      const key = [
        this.selectedLauncher.app_id,
        this.selectedGame.game_id,
        this.selectedSession.id,
      ].join('|')
      // Guard: each borrow POST is logged as an event by the backend, so don't
      // re-call it for the same selection within one open. If we already have the
      // result for this exact app/game/session, just re-show it.
      if (
        key === this.lastBorrowKey &&
        this.result &&
        this.resultAccounts.length
      ) {
        this.step = 3
        this.showResult()
        return
      }
      this.borrowing = true
      this.borrowError = ''
      try {
        const response = await SharedAccountsApi.borrow(this.arenaId, {
          app_id: this.selectedLauncher.app_id,
          game_id: this.selectedGame.game_id,
          session_id: this.selectedSession.id,
        })
        this.result = response.data || null
        this.lastBorrowKey = key
        this.step = 3
        // Guard against an unexpected 200 shape (not { first, second }): without
        // this, step 3 would render blank with no feedback.
        if (!this.resultAccounts.length) {
          this.borrowError = this.$t('sharedAccounts.borrow.borrowFailed')
          return
        }
        this.showResult()
      } catch (e) {
        // Do NOT log: borrow errors can carry sensitive context.
        const status = e && e.response && e.response.status
        this.borrowError =
          status === 404
            ? this.$t('sharedAccounts.borrow.noAccount')
            : this.$t('sharedAccounts.borrow.borrowFailed')
        this.step = 3
      } finally {
        this.borrowing = false
      }
    },
    // Show the borrow result on step 3. Preserves any already-fetched 2FA code
    // (consumed on read) - only polls for accounts that don't have one yet, so
    // re-showing the same borrow doesn't burn a fresh code.
    showResult() {
      this.startTick()
      this.resultAccounts.forEach((a) => {
        const current = a.id && this.twoFa[a.id]
        if (!current || current.status !== 'done') this.startTwoFa(a)
      })
    },
    // ---- 2FA ----
    stopPoll(id) {
      if (this.pollTimers[id]) {
        clearInterval(this.pollTimers[id])
        delete this.pollTimers[id]
      }
    },
    startTwoFa(acct) {
      const id = acct.id
      // No account id -> nothing to poll; the template shows an unavailable state.
      if (!id) return
      this.$set(this.twoFa, id, { code: '', status: 'polling', expiresAt: 0 })
      this.stopPoll(id)
      const startedAt = Date.now()
      const poll = async () => {
        // Stop if we already have a code or the dialog closed. The code is
        // consumed on read, so we must not fetch (and burn) one nobody sees.
        const current = this.twoFa[id]
        if (!this.value || (current && current.status === 'done')) {
          this.stopPoll(id)
          return
        }
        try {
          const response = await SharedAccountsApi.twoFaCode(this.arenaId, id)
          if (!this.value) {
            this.stopPoll(id)
            return
          }
          const code = response.data && response.data.code
          if (code) {
            this.$set(this.twoFa, id, {
              code,
              status: 'done',
              expiresAt: Date.now() + TWO_FA_TTL_MS,
            })
            this.stopPoll(id)
          }
        } catch (e) {
          // 404 = code not arrived yet; keep polling until the window elapses.
          const status = e && e.response && e.response.status
          if (status !== 404 || Date.now() - startedAt > POLL_MAX_MS) {
            this.$set(this.twoFa, id, {
              code: '',
              status: 'error',
              expiresAt: 0,
            })
            this.stopPoll(id)
          }
        }
      }
      // Assign the timer BEFORE the first poll so an immediate success can
      // actually clear it (otherwise the interval would never be stopped).
      this.pollTimers[id] = setInterval(poll, POLL_MS)
      poll()
    },
    twoFaOf(acct) {
      return (
        this.twoFa[acct.id] || { code: '', status: 'polling', expiresAt: 0 }
      )
    },
    startTick() {
      if (this.tickTimer) return
      this.tickTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 1000)
    },
    countdown(acct) {
      const exp = this.twoFaOf(acct).expiresAt
      // reference nowTick so this recomputes each second
      const remaining = exp - (this.nowTick || Date.now())
      if (remaining <= 0) return '0:00'
      const total = Math.floor(remaining / 1000)
      const m = Math.floor(total / 60)
      const s = ('0' + (total % 60)).slice(-2)
      return `${m}:${s}`
    },
    // ---- misc ----
    async copy(text) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(String(text))
        this.showSnackbar({
          color: 'success',
          message: this.$t('sharedAccounts.borrow.copied'),
        })
      } catch (e) {
        // ignore clipboard failures
      }
    },
    releaseEarly() {
      if (!this.selectedSession) return
      this.confirmRelease = true
    },
    async doRelease() {
      if (!this.selectedSession) return
      this.returning = true
      try {
        await SharedAccountsApi.returnSession(this.arenaId, {
          session_id: this.selectedSession.id,
        })
        this.confirmRelease = false
        this.showSnackbar({
          color: 'success',
          message: this.$t('sharedAccounts.borrow.released'),
        })
        this.$emit('released')
        this.close()
      } catch (e) {
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.borrow.borrowFailed'),
        })
      } finally {
        this.returning = false
      }
    },
  },
}
</script>

<style scoped>
.two-fa__code {
  font-family: 'Roboto Mono', monospace;
  font-size: 2.25rem;
  font-weight: 700;
  letter-spacing: 0.25rem;
  line-height: 1.2;
}
</style>
